import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import api, { errMsg, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, NoticeRegion } from "../lib/notices";
import { Plus, Video, FileText, Loader2, Users, X, Mail, CheckCircle2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_LABEL = {
  draft: "DRAFT", transcribing:"TRANSCRIBING", scripting:"SCRIPTING", voicing:"VOICING",
  rendering:"RENDERING", manual:"WRITING MANUAL", ready:"READY", failed:"FAILED",
};

export default function Dashboard() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [projects, setProjects] = useState(null);
  const [welcomeTeams, setWelcomeTeams] = useState([]);
  const [dismissVerify, setDismissVerify] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r)=>setProjects(r.data)).catch((e)=>{
      if (process.env.NODE_ENV === "development") console.warn("dashboard load failed", e);
      nav("/login");
    });
    try {
      const raw = localStorage.getItem("welcomeTeams");
      if (raw) {
        const list = JSON.parse(raw);
        if (Array.isArray(list) && list.length) setWelcomeTeams(list);
      }
      setDismissVerify(localStorage.getItem("verifyBannerDismissed") === "1");
    } catch (e) {
      // Corrupt localStorage entries — safe to ignore, but log in dev
      if (process.env.NODE_ENV === "development") console.warn("welcomeTeams parse failed", e);
    }
  }, [nav]);

  const dismissWelcome = () => {
    localStorage.removeItem("welcomeTeams");
    setWelcomeTeams([]);
  };

  const closeVerifyBanner = () => {
    localStorage.setItem("verifyBannerDismissed", "1");
    setDismissVerify(true);
  };

  const resendVerify = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent. Check your inbox.");
      await refresh();
    } catch (err) {
      toast.error(errMsg(err, "Could not send verification email"));
    } finally {
      setResending(false);
    }
  };

  const showVerifyBanner = user && user.email_verified === false && !dismissVerify;

  return (
    <div className="min-h-screen">
      <Header />
      <main id="main" tabIndex={-1} className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <NoticeRegion />
        {showVerifyBanner && (
          <div className="brutal-card mb-6 bg-state-danger-surface flex flex-wrap items-start justify-between gap-3" data-testid="verify-email-banner">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-white brutal-border flex items-center justify-center text-ink-accent flex-shrink-0">
                <Mail size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-black type-subheading tracking-tight">Confirm your email address.</div>
                <div className="text-sm text-ink-muted mt-1">
                  We sent a verification link to <span className="font-mono-ui font-bold">{user?.email}</span>. Verifying unlocks password reset and prevents your team invites from bouncing.
                </div>
                <button onClick={resendVerify} disabled={resending} className="brutal-btn brutal-btn-secondary mt-3 inline-flex" data-testid="btn-resend-verify">
                  {resending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Resend verification email
                </button>
              </div>
            </div>
            <button onClick={closeVerifyBanner} className="brutal-btn brutal-btn-ghost !p-2" aria-label="Dismiss" data-testid="verify-banner-dismiss">
              <X size={16} />
            </button>
          </div>
        )}
        {welcomeTeams.length > 0 && (
          <div className="brutal-card mb-8 bg-state-warning-surface flex flex-wrap items-start justify-between gap-3" data-testid="welcome-teams-banner">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-brand brutal-border flex items-center justify-center text-ink-on-accent flex-shrink-0">
                <Users size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-black type-subheading tracking-tight">
                  Welcome — you&apos;re in {welcomeTeams.length === 1 ? "a new workspace" : `${welcomeTeams.length} new workspaces`}.
                </div>
                <div className="text-sm text-ink-muted mt-1">
                  {welcomeTeams.map((t, i) => (
                    <span key={t.team_id}>
                      <span className="font-mono-ui font-bold">{t.name}</span>
                      <span className="text-2xs uppercase tracking-widest ml-1 text-ink-subtle">· {t.role}</span>
                      {i < welcomeTeams.length - 1 ? ", " : ""}
                    </span>
                  ))}
                  <span> — team projects now appear below.</span>
                </div>
                <Link to="/teams" className="brutal-btn brutal-btn-secondary mt-3 inline-flex" data-testid="welcome-open-teams">
                  <Users size={14} /> View teams
                </Link>
              </div>
            </div>
            <button onClick={dismissWelcome} className="brutal-btn brutal-btn-ghost !p-2" aria-label="Dismiss" data-testid="welcome-dismiss">
              <X size={16} />
            </button>
          </div>
        )}
        <div className="flex flex-wrap justify-between items-end gap-4 mb-10">
          <div>
            <div className="marquee-tag mb-2">// DASHBOARD</div>
            <h1 className="font-display font-black type-title tracking-tighter">PROJECTS.</h1>
          </div>
          <Link to="/new" className="brutal-btn" data-testid="new-project-cta"><Plus size={16}/> New Project</Link>
        </div>

        {projects === null && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="loading-projects">
            {/* The skeleton itself is aria-hidden, so without this the
                wait is silent for screen reader users. */}
            <p className="sr-only" role="status">Loading projects…</p>
            {[0,1,2].map(i=>(
              <div key={i} className="brutal-card" aria-hidden>
                <div className="flex justify-between mb-3">
                  <div className="h-4 w-24 bg-surface-sunken animate-pulse"/>
                  <div className="h-4 w-16 bg-surface-sunken animate-pulse"/>
                </div>
                <div className="h-6 w-3/4 bg-surface-sunken animate-pulse mb-3"/>
                <div className="h-4 w-full bg-surface animate-pulse mb-1"/>
                <div className="h-4 w-2/3 bg-surface animate-pulse mb-6"/>
                <div className="flex justify-between">
                  <div className="h-3 w-16 bg-surface-sunken animate-pulse"/>
                  <div className="h-3 w-24 bg-surface-sunken animate-pulse"/>
                </div>
              </div>
            ))}
          </div>
        )}

        {projects && projects.length === 0 && (
          <div className="brutal-card p-16 text-center" data-testid="empty-state">
            <div className="marquee-tag mb-4">// EMPTY</div>
            <h2 className="font-display font-black type-heading mb-4 tracking-tighter">NO PROJECTS YET.</h2>
            <p className="text-ink-muted mb-6">Start your first demo video or product manual now.</p>
            <Link to="/new" className="brutal-btn" data-testid="empty-cta"><Plus size={16}/> Create Project</Link>
          </div>
        )}

        {projects && projects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((p) => (
              <Link key={p.project_id} to={`/project/${p.project_id}`} className="brutal-card brutal-card-interactive block" data-testid={`project-card-${p.project_id}`}>
                {/* Decorative: the title below names the project. */}
                <div className={`brutal-border aspect-video overflow-hidden mb-4 flex items-center justify-center ${p.thumbnail_path ? "bg-surface-inverse" : "bg-surface-sunken"}`} data-testid={`project-thumb-${p.project_id}`}>
                  {p.thumbnail_path ? (
                    <img src={fileUrl(p.thumbnail_path)} alt="" loading="lazy" className="w-full h-full object-contain"/>
                  ) : p.kind === "product_manual" ? (
                    <FileText size={32} className="text-ink-subtle" aria-hidden/>
                  ) : (
                    <Video size={32} className="text-ink-subtle" aria-hidden/>
                  )}
                </div>
                <div className="flex items-start justify-between mb-3">
                  <div className="marquee-tag">{p.kind === "product_manual" ? "MANUAL" : p.kind === "demo_video" ? "VIDEO" : "VIDEO + MANUAL"}</div>
                  <div className="text-xs uppercase tracking-widest flex items-center">
                    <span className={`status-dot status-${p.status}`}></span>
                    {STATUS_LABEL[p.status] || p.status}
                    {p.status !== "ready" && p.status !== "draft" && p.status !== "failed" && (
                      <Loader2 size={12} className="ml-1 animate-spin"/>
                    )}
                  </div>
                </div>
                <h3 className="font-display font-black type-subheading mb-2 tracking-tight leading-tight">{p.title}</h3>
                <p className="text-sm text-ink-muted line-clamp-2 mb-4 min-h-[2.5rem]">{p.description || "No description"}</p>
                {p.status !== "draft" && p.status !== "ready" && (
                  <div
                    className="progress-bar mb-2"
                    role="progressbar"
                    aria-label={`${p.title} render progress`}
                    aria-valuenow={p.progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div className="progress-fill" style={{width:`${p.progress}%`}}/>
                  </div>
                )}
                <div className="flex justify-between text-xs uppercase tracking-widest text-ink-subtle mt-2">
                  <span>{p.screenshots?.length || 0} SHOTS</span>
                  <span>{formatDistanceToNow(new Date(p.created_at), {addSuffix:true})}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
